import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Button, Card, PageHeader, cx } from "../components/ui";
import type { Contact, ContactWrite, LifecycleStage } from "../api/types";
import { plural } from "../lib/format";

const STAGES: LifecycleStage[] = [
  "lead",
  "prospect",
  "active",
  "inactive",
  "lost",
  "vendor",
];

const SOURCES = [
  ["", "Not set"],
  ["referral", "Referral"],
  ["walk_in", "Walk in"],
  ["website", "Website"],
  ["phone", "Phone"],
  ["social", "Social"],
  ["campaign", "Campaign"],
  ["marketplace", "Marketplace"],
  ["other", "Other"],
] as const;

/**
 * Create and edit, in one form.
 *
 * The same component serves both because the fields and the rules are identical
 * -- a "new contact" screen and an "edit contact" screen drift apart otherwise,
 * and the drift shows up as a field the edit form forgets.
 *
 * `account` is never sent. The tenant comes from the token, and an account id in
 * a request body is an attack surface (see `TenantSerializer`).
 */
export function ContactFormPage() {
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const label = user?.account?.primary_object_label ?? "Contact";

  const existing = useQuery({
    queryKey: ["contact", id],
    queryFn: ({ signal }) => api.contact(id!, signal),
    enabled: editing,
  });

  if (editing && existing.isPending) {
    return (
      <>
        <BackLink editing={editing} />
        <PageHeader title="Loading…" />
      </>
    );
  }

  if (editing && existing.isError) {
    return (
      <>
        <BackLink editing={editing} />
        <PageHeader title="Not available" subtitle="That record could not be loaded." />
      </>
    );
  }

  return (
    <>
      <BackLink editing={editing} />
      <PageHeader
        title={editing ? `Edit ${existing.data?.display_name}` : `New ${label.toLowerCase()}`}
        subtitle={
          editing
            ? "Changes are visible immediately to everyone on the account."
            : "A name and one way to reach them is enough to start."
        }
      />
      <ContactForm
        contact={existing.data}
        label={label}
        onDone={(saved) => {
          void queryClient.invalidateQueries({ queryKey: ["contacts"] });
          void queryClient.invalidateQueries({ queryKey: ["usage"] });
          void queryClient.invalidateQueries({ queryKey: ["timeline", saved.id] });
          navigate(`/customers/contacts/${saved.id}`);
        }}
      />
    </>
  );
}

function BackLink({ editing }: { editing: boolean }) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const name = plural(user?.account?.primary_object_label ?? "Contact");

  // Editing goes back to the record; creating goes back to the list.
  const target = editing && id ? `/customers/contacts/${id}` : "/customers/contacts";

  return (
    <button
      onClick={() => navigate(target)}
      className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
    >
      <ArrowLeft size={14} /> Back to {name}
    </button>
  );
}

function ContactForm({
  contact,
  label,
  onDone,
}: {
  contact: Contact | undefined;
  /** The industry's singular name for one record: Client, Patient, Buyer. */
  label: string;
  onDone: (saved: Contact) => void;
}) {
  const [form, setForm] = useState<ContactWrite>(() => ({
    first_name: contact?.first_name ?? "",
    last_name: contact?.last_name ?? "",
    company_name: contact?.company_name ?? "",
    email: contact?.email ?? "",
    phone: contact?.phone ?? "",
    whatsapp: contact?.whatsapp ?? "",
    city: contact?.city ?? "",
    state: contact?.state ?? "",
    postal_code: contact?.postal_code ?? "",
    lifecycle_stage: (contact?.lifecycle_stage ?? "lead") as LifecycleStage,
    source: contact?.source ?? "",
    tags: (contact?.tags as string[] | undefined) ?? [],
    notes: contact?.notes ?? "",
    marketing_opt_in: contact?.marketing_opt_in ?? false,
  }));

  const [tagDraft, setTagDraft] = useState("");

  const save = useMutation({
    mutationFn: (body: ContactWrite) =>
      contact ? api.updateContact(contact.id, body) : api.createContact(body),
    onSuccess: onDone,
  });

  const error = save.error instanceof ApiError ? save.error : null;
  const fieldErrors = error?.fields ?? {};

  function set<K extends keyof ContactWrite>(key: K, value: ContactWrite[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function addTag(raw: string) {
    const tag = raw.trim().replace(/,$/, "");
    if (!tag) return;
    const existing = form.tags ?? [];
    // Case-insensitive dedupe: "VIP" and "vip" are the same tag to a human, and
    // two spellings would split the filter.
    if (existing.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      setTagDraft("");
      return;
    }
    set("tags", [...existing, tag]);
    setTagDraft("");
  }

  function removeTag(tag: string) {
    set("tags", (form.tags ?? []).filter((t) => t !== tag));
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    save.mutate({
      ...form,
      // The API takes "" for an empty optional string; sending null for a
      // CharField is a 400 that reads like a server fault.
      source: form.source || undefined,
    });
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <Card>
          <h2 className="mb-4 text-sm font-semibold">Who they are</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="first_name"
              label="First name"
              value={form.first_name ?? ""}
              onChange={(v) => set("first_name", v)}
              errors={fieldErrors.first_name}
              autoFocus={!contact}
            />
            <TextField
              id="last_name"
              label="Last name"
              // Required by the model, but the API accepts a company name
              // instead -- the serializer decides, not the browser.
              required={!form.company_name}
              value={form.last_name ?? ""}
              onChange={(v) => set("last_name", v)}
              errors={fieldErrors.last_name}
            />
            <div className="sm:col-span-2">
              <TextField
                id="company_name"
                label="Company"
                hint="Optional. Enough on its own if this is a business account."
                value={form.company_name ?? ""}
                onChange={(v) => set("company_name", v)}
                errors={fieldErrors.company_name}
              />
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold">How to reach them</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="email"
              label="Email"
              type="email"
              value={form.email ?? ""}
              onChange={(v) => set("email", v)}
              errors={fieldErrors.email}
            />
            <TextField
              id="phone"
              label="Phone"
              type="tel"
              hint="Search by phone works across formats once this is set."
              value={form.phone ?? ""}
              onChange={(v) => set("phone", v)}
              errors={fieldErrors.phone}
            />
            <TextField
              id="whatsapp"
              label="WhatsApp"
              value={form.whatsapp ?? ""}
              onChange={(v) => set("whatsapp", v)}
              errors={fieldErrors.whatsapp}
            />
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.marketing_opt_in ?? false}
                  onChange={(e) => set("marketing_opt_in", e.target.checked)}
                  className="size-4 rounded border-slate-300"
                />
                Marketing opt-in
              </label>
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold">Notes</h2>
          <textarea
            id="notes"
            rows={4}
            value={form.notes ?? ""}
            onChange={(e) => set("notes", e.target.value)}
            className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
          {fieldErrors.notes ? (
            <p className="mt-1 text-sm text-rose-600">{fieldErrors.notes.join(" ")}</p>
          ) : null}
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <h2 className="mb-4 text-sm font-semibold">Classification</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="lifecycle_stage" className="block text-sm font-medium">
                Stage
              </label>
              <select
                id="lifecycle_stage"
                value={form.lifecycle_stage ?? "lead"}
                onChange={(e) => set("lifecycle_stage", e.target.value as LifecycleStage)}
                className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
              >
                {STAGES.map((stage) => (
                  <option key={stage} value={stage} className="capitalize">
                    {stage}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="source" className="block text-sm font-medium">
                How they found you
              </label>
              <select
                id="source"
                value={form.source ?? ""}
                onChange={(e) => set("source", e.target.value)}
                className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
              >
                {SOURCES.map(([value, text]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="mb-1 text-sm font-semibold">Tags</h2>
          <p className="mb-3 text-xs text-slate-500">
            Ad-hoc labels for filtering. Press Enter to add.
          </p>

          <div className="flex flex-wrap gap-1.5">
            {(form.tags ?? []).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2 pr-1 text-xs font-medium text-slate-700"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  aria-label={`Remove tag ${tag}`}
                  className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                >
                  <X size={11} />
                </button>
              </span>
            ))}
            {(form.tags ?? []).length === 0 ? (
              <span className="text-xs text-slate-400">No tags yet</span>
            ) : null}
          </div>

          <input
            id="tag"
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                // Otherwise Enter submits the whole form, which loses the draft.
                e.preventDefault();
                addTag(tagDraft);
              }
            }}
            onBlur={() => addTag(tagDraft)}
            placeholder="Add a tag"
            className="mt-3 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </Card>

        <Card>
          <h2 className="mb-3 text-sm font-semibold">Address</h2>
          <div className="space-y-3">
            <TextField
              id="city"
              label="City"
              value={form.city ?? ""}
              onChange={(v) => set("city", v)}
              errors={fieldErrors.city}
            />
            <TextField
              id="state"
              label="State"
              value={form.state ?? ""}
              onChange={(v) => set("state", v)}
              errors={fieldErrors.state}
            />
            <TextField
              id="postal_code"
              label="Postal code"
              value={form.postal_code ?? ""}
              onChange={(v) => set("postal_code", v)}
              errors={fieldErrors.postal_code}
            />
          </div>
        </Card>
      </div>

      <div className="lg:col-span-3">
        {error && !Object.keys(fieldErrors).length ? (
          <p role="alert" className="mb-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error.message}
          </p>
        ) : null}

        <div className="flex items-center gap-2">
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? "Saving…" : contact ? "Save changes" : `Create ${label.toLowerCase()}`}
          </Button>
          <Button onClick={() => window.history.back()} disabled={save.isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  errors,
  hint,
  type = "text",
  required,
  autoFocus,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  errors?: string[] | undefined;
  hint?: string | undefined;
  type?: string | undefined;
  required?: boolean | undefined;
  autoFocus?: boolean | undefined;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={errors?.length ? true : undefined}
        aria-describedby={cx(errors?.length ? `${id}-error` : undefined, hint ? `${id}-hint` : undefined) || undefined}
        className={cx(
          "mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset placeholder:text-slate-400 focus:ring-2 focus:ring-inset",
          errors?.length ? "ring-rose-400" : "ring-slate-300 focus:ring-slate-900",
        )}
      />
      {errors?.length ? (
        <p id={`${id}-error`} role="alert" className="mt-1 text-sm text-rose-600">
          {errors.join(" ")}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}